import React, { useEffect, useMemo, useState } from "react";
const BASE = import.meta?.env?.BASE_URL ?? "/";
const F1_CONFIG = {
  title: "F1 — DK Dashboard",
  driversSrc: `${BASE}data/f1/latest/f1_driver_sims.json`,
  constructorsSrc: `${BASE}data/f1/latest/f1_constructor_sims.json`,
  distributionsSrc: `${BASE}data/f1/latest/f1_driver_distributions.json`,
  metaSrc: `${BASE}data/f1/latest/meta.json`,
  driverLogoBase: `${BASE}logos/F1/Drivers`,
  teamLogoBase: `${BASE}logos/F1/Teams`,
};
function useJson(url) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    fetch(url, { cache: "no-store" })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((json) => {
        if (!alive) return;
        setData(json);
      })
      .catch((e) => {
        if (!alive) return;
        setError(e);
        setData(null);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [url]);
  return { data, loading, error };
}
function useLastUpdated(metaUrl) {
  const [updatedAt, setUpdatedAt] = useState(null);
  useEffect(() => {
    let alive = true;
    fetch(`${metaUrl}?_=${Date.now()}`, { cache: "no-store" })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((meta) => {
        if (!alive) return;
        const iso =
          meta?.updated_iso ||
          meta?.updated_utc ||
          meta?.updated ||
          meta?.lastUpdated ||
          null;
        if (iso) setUpdatedAt(new Date(iso));
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [metaUrl]);
  return updatedAt;
}
const num = (v) => {
  const n = Number(String(v ?? "").replace(/[,%$]/g, ""));
  return Number.isFinite(n) ? n : NaN;
};
const pct = (v, digits = 1) => {
  const n = num(v);
  if (!Number.isFinite(n)) return "–";
  return `${(n * 100).toFixed(digits)}%`;
};
const one = (v) => {
  const n = num(v);
  return Number.isFinite(n) ? n.toFixed(1) : "–";
};
const two = (v) => {
  const n = num(v);
  return Number.isFinite(n) ? n.toFixed(2) : "–";
};
const money = (v) => {
  const n = num(v);
  return Number.isFinite(n)
    ? n.toLocaleString(undefined, { maximumFractionDigits: 0 })
    : "–";
};
const fmtUpdated = (d) =>
  d
    ? d.toLocaleString(undefined, {
        month: "numeric",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      })
    : null;
const keynorm = (s) =>
  String(s ?? "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
const driverLogoKey = (name) => {
  const cleaned = String(name ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\b(jr|jr\.|sr|sr\.|ii|iii|iv|v)\b/gi, "")
    .replace(/[^a-zA-Z0-9\s-]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (!cleaned) return "";
  const parts = cleaned.split(" ");
  return parts[parts.length - 1].toLowerCase();
};
function probToAmerican(prob) {
  const p = num(prob);
  if (!Number.isFinite(p) || p <= 0 || p >= 1) return "–";
  const american = p >= 0.5 ? (-100 * p) / (1 - p) : (100 * (1 - p)) / p;
  const rounded = Math.round(american);
  return rounded > 0 ? `+${rounded}` : `${rounded}`;
}
function probToDecimal(prob) {
  const p = num(prob);
  if (!Number.isFinite(p) || p <= 0 || p > 1) return "–";
  return (1 / p).toFixed(2);
}
function cmp(a, b, dir = "asc") {
  const na = num(a);
  const nb = num(b);
  const aNum = Number.isFinite(na);
  const bNum = Number.isFinite(nb);
  if (aNum && bNum) return dir === "asc" ? na - nb : nb - na;
  const sa = String(a ?? "").toLowerCase();
  const sb = String(b ?? "").toLowerCase();
  if (sa < sb) return dir === "asc" ? -1 : 1;
  if (sa > sb) return dir === "asc" ? 1 : -1;
  return 0;
}
function csvEscape(value) {
  const v = value == null ? "" : String(value);
  if (/[",\n]/.test(v)) return `"${v.replace(/"/g, '""')}"`;
  return v;
}
function downloadCsv(filename, headers, rows) {
  const csv = [
    headers.map(csvEscape).join(","),
    ...rows.map((row) => row.map(csvEscape).join(",")),
  ].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
function SegTabs({ tabs, value, onChange, isDark }) {
  return (
    <div
      className={
        "inline-flex flex-wrap items-center gap-1 " +
        (isDark
          ? "bg-transparent"
          : "bg-transparent")
      }
    >
      {tabs.map((tab) => {
        const active = value === tab.key;
        return (
          <button
            key={tab.key}
            onClick={() => onChange(tab.key)}
            className={
              "border-b-2 px-3 py-2.5 text-xs font-semibold transition " +
              (active
                ? (isDark ? "border-sky-400 text-white" : "border-sky-500 text-slate-900")
                : isDark
                ? "border-transparent text-slate-400 hover:text-white"
                : "border-transparent text-slate-500 hover:text-slate-900")
            }
          >
            {tab.label}
          </button>
        );
      })}
    </div>
  );
}
function LogoImg({ src, alt, className }) {
  return (
    <img
      src={src}
      alt={alt}
      className={className}
      onError={(e) => {
        e.currentTarget.style.display = "none";
      }}
    />
  );
}
function ActionButton({ onClick, children, isDark }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        "h-8 rounded-md border px-3 text-xs font-semibold transition " +
        (isDark
          ? "border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800"
          : "border-slate-300 bg-white text-slate-800 hover:bg-slate-100")
      }
    >
      {children}
    </button>
  );
}
function SectionCard({ title, subtitle, right, children, isDark }) {
  return (
    <div
      className={
        "min-w-0 rounded-2xl border p-4 shadow-sm " +
        (isDark
          ? "bg-slate-900/95 border-slate-800"
          : "bg-white border-slate-200")
      }
    >
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-sm font-extrabold">{title}</div>
          {subtitle ? (
            <div
              className={
                isDark ? "text-xs text-slate-400" : "text-xs text-slate-500"
              }
            >
              {subtitle}
            </div>
          ) : null}
        </div>
        {right}
      </div>
      {children}
    </div>
  );
}
function DataTable({
  columns,
  rows,
  sort,
  setSort,
  isDark,
  stickyFirst = false,
  exportFilename,
  exportRows,
}) {
  const headerBg = isDark
    ? "bg-slate-900 text-slate-200"
    : "bg-white text-slate-800";
  const headerBorder = isDark ? "border-slate-800" : "border-slate-200";
  const rowOdd = isDark ? "bg-slate-950" : "bg-white";
  const rowEven = isDark ? "bg-slate-900/70" : "bg-slate-50";
  const stickyOdd = isDark ? "bg-slate-950" : "bg-white";
  const stickyEven = isDark ? "bg-slate-900" : "bg-slate-50";
  const handleExport = () => {
    if (!exportRows?.length) return;
    downloadCsv(
      exportFilename || "table_export.csv",
      exportRows.map((c) => c.label),
      rows.map((row) => exportRows.map((c) => c.value(row)))
    );
  };
  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <ActionButton onClick={handleExport} isDark={isDark}>
          Export CSV
        </ActionButton>
      </div>
      <div className={"overflow-auto max-h-[68vh] rounded-xl border " + (isDark ? "border-slate-800" : "border-slate-200")}>
        <table className="w-full text-xs border-separate" style={{ borderSpacing: 0 }}>
          <thead>
            <tr>
              {columns.map((col, idx) => {
                const active = sort.col === col.key;
                const arrow = active ? (sort.dir === "asc" ? "▲" : "▼") : "▲";
                return (
                  <th
                    key={col.key}
                    onClick={() =>
                      setSort((s) =>
                        s.col === col.key
                          ? { col: col.key, dir: s.dir === "asc" ? "desc" : "asc" }
                          : { col: col.key, dir: col.defaultDir || "desc" }
                      )
                    }
                    className={[
                      "sticky top-0 z-10 px-3 py-2 text-center font-bold border-b cursor-pointer select-none whitespace-nowrap",
                      headerBg,
                      headerBorder,
                      stickyFirst && idx === 0 ? "left-0 z-20 text-left" : "",
                    ].join(" ")}
                  >
                    <div
                      className={
                        idx === 0
                          ? "flex items-center gap-1"
                          : "flex items-center justify-center gap-1"
                      }
                    >
                      {col.label}{" "}
                      <span className="text-[10px] opacity-60">{arrow}</span>
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => {
              const rowBg = i % 2 === 0 ? rowOdd : rowEven;
              const stickyBg = i % 2 === 0 ? stickyOdd : stickyEven;
              return (
                <tr key={row.__key || `${i}-${row.Name || "row"}`} className={rowBg}>
                  {columns.map((col, idx) => {
                    const raw = row[col.key];
                    const content = col.render ? col.render(row) : raw ?? "–";
                    return (
                      <td
                        key={col.key}
                        className={[
                          "px-3 py-2 border-b tabular-nums align-middle",
                          isDark ? "border-slate-800/80" : "border-slate-200",
                          stickyFirst && idx === 0 ? `${stickyBg} sticky left-0 z-[2]` : "",
                          idx === 0 ? "text-left" : "text-center",
                        ].join(" ")}
                      >
                        {content}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
            {!rows.length && (
              <tr>
                <td colSpan={columns.length} className="px-3 py-6 text-center text-slate-500">
                  No rows found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
function DistCell({ value, max, isDark }) {
  const n = num(value);
  const t =
    Number.isFinite(n) && max > 0 ? Math.max(0, Math.min(1, n / max)) : 0;
  const bg = isDark
    ? `rgba(56,189,248,${0.08 + t * 0.75})`
    : `rgba(14,165,233,${0.04 + t * 0.45})`;
  return (
    <div
      className={
        "min-w-[52px] rounded-md px-2 py-1 text-center font-semibold border " +
        (isDark
          ? "border-slate-800 text-slate-100"
          : "border-slate-200 text-slate-900")
      }
      style={{ backgroundColor: bg }}
    >
      {pct(n, 1)}
    </div>
  );
}
export default function F1Projections() {
  const {
    data: driversRaw,
    loading: driversLoading,
    error: driversError,
  } = useJson(F1_CONFIG.driversSrc);
  const {
    data: constructorsRaw,
    loading: constructorsLoading,
    error: constructorsError,
  } = useJson(F1_CONFIG.constructorsSrc);
  const {
    data: distributionsRaw,
    loading: distributionsLoading,
    error: distributionsError,
  } = useJson(F1_CONFIG.distributionsSrc);
  const updatedAt = useLastUpdated(F1_CONFIG.metaSrc);
  const [theme, setTheme] = useState("dark");
  const [tab, setTab] = useState("projections");
  const [search, setSearch] = useState("");
  const [projectionView, setProjectionView] = useState("drivers");
  const [bettingView, setBettingView] = useState("drivers");
  const [driverMode, setDriverMode] = useState("driver"); // driver | captain
  const [projSort, setProjSort] = useState({
    col: "Driver_DK_Proj",
    dir: "desc",
  });
  const [constructorProjSort, setConstructorProjSort] = useState({
    col: "DK Proj",
    dir: "desc",
  });
  const [bettingSort, setBettingSort] = useState({ col: "WinPct", dir: "desc" });
  const [constructorBettingSort, setConstructorBettingSort] = useState({
    col: "WinPct",
    dir: "desc",
  });
  const [distSort, setDistSort] = useState({ col: "P1", dir: "desc" });
  const isDark = theme === "dark";
  const isCaptain = driverMode === "captain";
  const drivers = useMemo(() => {
    const arr = Array.isArray(driversRaw)
      ? driversRaw
      : Array.isArray(driversRaw?.drivers)
      ? driversRaw.drivers
      : [];
    return arr.map((r) => {
      const salary = num(r?.Salary);
      const proj = num(r?.Driver_DK_Proj);
      const captainSalary = Number.isFinite(salary) ? salary * 1.5 : NaN;
      const captainProj = Number.isFinite(proj) ? proj * 1.5 : NaN;
      const captainVal =
        Number.isFinite(captainProj) && Number.isFinite(captainSalary) && captainSalary > 0
          ? (captainProj / captainSalary) * 1000
          : NaN;
      return {
        ...r,
        Captain_Salary: captainSalary,
        Captain_DK_Proj: captainProj,
        Captain_DK_Val: captainVal,
        __key: keynorm(r?.Name),
      };
    });
  }, [driversRaw]);
  const constructors = useMemo(() => {
    const arr = Array.isArray(constructorsRaw)
      ? constructorsRaw
      : Array.isArray(constructorsRaw?.constructors)
      ? constructorsRaw.constructors
      : [];
    return arr.map((r) => ({ ...r, __key: keynorm(r?.Name || r?.TeamAbbrev) }));
  }, [constructorsRaw]);
  const distributions = useMemo(() => {
    const arr = Array.isArray(distributionsRaw)
      ? distributionsRaw
      : Array.isArray(distributionsRaw?.driver_distributions)
      ? distributionsRaw.driver_distributions
      : [];
    return arr.map((r) => ({ ...r, __key: keynorm(r?.Name) }));
  }, [distributionsRaw]);
  const driverSearchRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return drivers;
    return drivers.filter((r) =>
      [r.Name, r.TeamAbbrev, r.StartPos, r.Salary]
        .join(" ")
        .toLowerCase()
        .includes(q)
    );
  }, [drivers, search]);
  const constructorSearchRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return constructors;
    return constructors.filter((r) =>
      [r.Name, r.TeamAbbrev, r.Salary].join(" ").toLowerCase().includes(q)
    );
  }, [constructors, search]);
  const distributionRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return distributions;
    return distributions.filter((r) =>
      [r.Name].join(" ").toLowerCase().includes(q)
    );
  }, [distributions, search]);
  const sortedDriverProjectionRows = useMemo(
    () =>
      [...driverSearchRows].sort((a, b) =>
        cmp(a?.[projSort.col], b?.[projSort.col], projSort.dir)
      ),
    [driverSearchRows, projSort]
  );
  const sortedConstructorProjectionRows = useMemo(
    () =>
      [...constructorSearchRows].sort((a, b) =>
        cmp(
          a?.[constructorProjSort.col],
          b?.[constructorProjSort.col],
          constructorProjSort.dir
        )
      ),
    [constructorSearchRows, constructorProjSort]
  );
  const sortedDriverBettingRows = useMemo(
    () =>
      [...driverSearchRows].sort((a, b) =>
        cmp(a?.[bettingSort.col], b?.[bettingSort.col], bettingSort.dir)
      ),
    [driverSearchRows, bettingSort]
  );
  const sortedConstructorBettingRows = useMemo(
    () =>
      [...constructorSearchRows].sort((a, b) =>
        cmp(
          a?.[constructorBettingSort.col],
          b?.[constructorBettingSort.col],
          constructorBettingSort.dir
        )
      ),
    [constructorSearchRows, constructorBettingSort]
  );
  const sortedDistributionRows = useMemo(
    () =>
      [...distributionRows].sort((a, b) =>
        cmp(a?.[distSort.col], b?.[distSort.col], distSort.dir)
      ),
    [distributionRows, distSort]
  );
  const distributionMax = useMemo(() => {
    let max = 0;
    for (const row of distributions) {
      for (let i = 1; i <= 22; i += 1) {
        max = Math.max(max, num(row?.[`P${i}`]) || 0);
      }
    }
    return max;
  }, [distributions]);
  const driverNameColumn = {
    key: "Name",
    label: "Driver",
    defaultDir: "asc",
    render: (row) => (
      <div className="flex items-center gap-2 min-w-[240px]">
        <LogoImg
          src={`${F1_CONFIG.driverLogoBase}/${driverLogoKey(row.Name)}.png`}
          alt={row.Name}
          className="h-9 w-9 rounded-full object-cover border border-slate-700"
        />
        <div className="min-w-0">
          <div className="font-bold truncate">{row.Name}</div>
          <div
            className={
              isDark ? "text-[11px] text-slate-400" : "text-[11px] text-slate-500"
            }
          >
            {row.TeamAbbrev}
          </div>
        </div>
      </div>
    ),
  };
  const projectionDriverColumns = [
    driverNameColumn,
    { key: "StartPos", label: "Start", render: (row) => one(row.StartPos) },
    {
      key: isCaptain ? "Captain_Salary" : "Salary",
      label: isCaptain ? "CPT Salary" : "Salary",
      render: (row) => money(isCaptain ? row.Captain_Salary : row.Salary),
    },
    {
      key: isCaptain ? "Captain_DK_Proj" : "Driver_DK_Proj",
      label: isCaptain ? "CPT Proj" : "DK Proj",
      render: (row) => two(isCaptain ? row.Captain_DK_Proj : row.Driver_DK_Proj),
    },
    {
      key: isCaptain ? "Captain_DK_Val" : "DK Val",
      label: isCaptain ? "CPT Val" : "DK Val",
      render: (row) => two(isCaptain ? row.Captain_DK_Val : row["DK Val"]),
    },
    { key: "Finish_mean", label: "Finish", render: (row) => two(row.Finish_mean) },
    { key: "LapsLed_mean", label: "Laps Led", render: (row) => two(row.LapsLed_mean) },
    {
      key: "FastestLapPct",
      label: "Fast Lap%",
      render: (row) => pct(row.FastestLapPct, 1),
    },
    {
      key: "ClassifiedPct",
      label: "Classified%",
      render: (row) => pct(row.ClassifiedPct, 1),
    },
    { key: "WinPct", label: "Win%", render: (row) => pct(row.WinPct, 1) },
    {
      key: "WinFairDecimal",
      label: "Win Fair",
      render: (row) => probToDecimal(row.WinPct),
    },
    { key: "Top3Pct", label: "Top 3%", render: (row) => pct(row.Top3Pct, 1) },
    { key: "Top5Pct", label: "Top 5%", render: (row) => pct(row.Top5Pct, 1) },
    { key: "Top10Pct", label: "Top 10%", render: (row) => pct(row.Top10Pct, 1) },
  ];
  const projectionConstructorColumns = [
    {
      key: "Name",
      label: "Constructor",
      defaultDir: "asc",
      render: (row) => (
        <div className="flex items-center gap-2 min-w-[220px]">
          <LogoImg
            src={`${F1_CONFIG.teamLogoBase}/${keynorm(row.TeamAbbrev || row.Name)}.png`}
            alt={row.Name}
            className="h-9 w-9 rounded-full object-contain border border-slate-700 bg-white"
          />
          <div className="min-w-0">
            <div className="font-bold truncate">{String(row.Name || "").trim()}</div>
            <div
              className={
                isDark ? "text-[11px] text-slate-400" : "text-[11px] text-slate-500"
              }
            >
              {row.TeamAbbrev}
            </div>
          </div>
        </div>
      ),
    },
    { key: "Salary", label: "Salary", render: (row) => money(row.Salary) },
    { key: "DK Proj", label: "DK Proj", render: (row) => two(row["DK Proj"]) },
    { key: "DK Val", label: "DK Val", render: (row) => two(row["DK Val"]) },
    { key: "WinPct", label: "Win%", render: (row) => pct(row.WinPct, 1) },
    {
      key: "WinFairDecimal",
      label: "Win Fair",
      render: (row) => probToDecimal(row.WinPct),
    },
    {
      key: "Both_Classified_Pct",
      label: "Both Classified%",
      render: (row) => pct(row.Both_Classified_Pct, 1),
    },
    {
      key: "Both_Points_Pct",
      label: "Both Points%",
      render: (row) => pct(row.Both_Points_Pct, 1),
    },
    {
      key: "Both_Podium_Pct",
      label: "Both Podium%",
      render: (row) => pct(row.Both_Podium_Pct, 1),
    },
    { key: "LapsLed_mean", label: "Laps Led", render: (row) => two(row.LapsLed_mean) },
    {
      key: "FastestLapPct",
      label: "Fast Lap%",
      render: (row) => pct(row.FastestLapPct, 1),
    },
  ];
  const bettingDriverColumns = [
    driverNameColumn,
    { key: "WinPct", label: "Win%", render: (row) => pct(row.WinPct, 2) },
    {
      key: "WinFairDecimal",
      label: "Win Fair",
      render: (row) => probToDecimal(row.WinPct),
    },
    {
      key: "WinFairAmerican",
      label: "Win Am",
      render: (row) => probToAmerican(row.WinPct),
    },
    { key: "Top3Pct", label: "Top 3%", render: (row) => pct(row.Top3Pct, 2) },
    {
      key: "Top3FairDecimal",
      label: "Top 3 Fair",
      render: (row) => probToDecimal(row.Top3Pct),
    },
    {
      key: "Top3FairAmerican",
      label: "Top 3 Am",
      render: (row) => probToAmerican(row.Top3Pct),
    },
    { key: "Top5Pct", label: "Top 5%", render: (row) => pct(row.Top5Pct, 2) },
    {
      key: "Top5FairDecimal",
      label: "Top 5 Fair",
      render: (row) => probToDecimal(row.Top5Pct),
    },
    {
      key: "Top5FairAmerican",
      label: "Top 5 Am",
      render: (row) => probToAmerican(row.Top5Pct),
    },
    { key: "Top10Pct", label: "Top 10%", render: (row) => pct(row.Top10Pct, 2) },
    {
      key: "Top10FairDecimal",
      label: "Top 10 Fair",
      render: (row) => probToDecimal(row.Top10Pct),
    },
    {
      key: "Top10FairAmerican",
      label: "Top 10 Am",
      render: (row) => probToAmerican(row.Top10Pct),
    },
  ];
  const bettingConstructorColumns = [
    {
      key: "Name",
      label: "Constructor",
      defaultDir: "asc",
      render: projectionConstructorColumns[0].render,
    },
    { key: "WinPct", label: "Win%", render: (row) => pct(row.WinPct, 2) },
    {
      key: "WinFairDecimal",
      label: "Win Fair",
      render: (row) => probToDecimal(row.WinPct),
    },
    {
      key: "WinFairAmerican",
      label: "Win Am",
      render: (row) => probToAmerican(row.WinPct),
    },
    {
      key: "Both_Classified_Pct",
      label: "Both Classified%",
      render: (row) => pct(row.Both_Classified_Pct, 2),
    },
    {
      key: "BothClassifiedDecimal",
      label: "Classified Fair",
      render: (row) => probToDecimal(row.Both_Classified_Pct),
    },
    {
      key: "BothClassifiedAm",
      label: "Classified Am",
      render: (row) => probToAmerican(row.Both_Classified_Pct),
    },
    {
      key: "Both_Points_Pct",
      label: "Both Points%",
      render: (row) => pct(row.Both_Points_Pct, 2),
    },
    {
      key: "BothPointsDecimal",
      label: "Points Fair",
      render: (row) => probToDecimal(row.Both_Points_Pct),
    },
    {
      key: "BothPointsAm",
      label: "Points Am",
      render: (row) => probToAmerican(row.Both_Points_Pct),
    },
    {
      key: "Both_Podium_Pct",
      label: "Both Podium%",
      render: (row) => pct(row.Both_Podium_Pct, 2),
    },
    {
      key: "BothPodiumDecimal",
      label: "Podium Fair",
      render: (row) => probToDecimal(row.Both_Podium_Pct),
    },
    {
      key: "BothPodiumAm",
      label: "Podium Am",
      render: (row) => probToAmerican(row.Both_Podium_Pct),
    },
    {
      key: "FastestLapPct",
      label: "Fast Lap%",
      render: (row) => pct(row.FastestLapPct, 2),
    },
  ];
  const projectionDriverExport = [
    { label: "Driver", value: (row) => row.Name },
    { label: "Team", value: (row) => row.TeamAbbrev },
    { label: "Mode", value: () => (isCaptain ? "Captain" : "Driver") },
    { label: "Start", value: (row) => row.StartPos },
    {
      label: isCaptain ? "CPT Salary" : "Salary",
      value: (row) => (isCaptain ? row.Captain_Salary : row.Salary),
    },
    {
      label: isCaptain ? "CPT Proj" : "DK Proj",
      value: (row) => (isCaptain ? row.Captain_DK_Proj : row.Driver_DK_Proj),
    },
    {
      label: isCaptain ? "CPT Val" : "DK Val",
      value: (row) => (isCaptain ? row.Captain_DK_Val : row["DK Val"]),
    },
    { label: "Finish", value: (row) => row.Finish_mean },
    { label: "Laps Led", value: (row) => row.LapsLed_mean },
    { label: "Fast Lap%", value: (row) => row.FastestLapPct },
    { label: "Classified%", value: (row) => row.ClassifiedPct },
    { label: "Win%", value: (row) => row.WinPct },
    { label: "Win Fair", value: (row) => probToDecimal(row.WinPct) },
    { label: "Top 3%", value: (row) => row.Top3Pct },
    { label: "Top 5%", value: (row) => row.Top5Pct },
    { label: "Top 10%", value: (row) => row.Top10Pct },
  ];
  const projectionConstructorExport = [
    { label: "Constructor", value: (row) => row.Name },
    { label: "Team", value: (row) => row.TeamAbbrev },
    { label: "Salary", value: (row) => row.Salary },
    { label: "DK Proj", value: (row) => row["DK Proj"] },
    { label: "DK Val", value: (row) => row["DK Val"] },
    { label: "Win%", value: (row) => row.WinPct },
    { label: "Win Fair", value: (row) => probToDecimal(row.WinPct) },
    { label: "Both Classified%", value: (row) => row.Both_Classified_Pct },
    { label: "Both Points%", value: (row) => row.Both_Points_Pct },
    { label: "Both Podium%", value: (row) => row.Both_Podium_Pct },
    { label: "Laps Led", value: (row) => row.LapsLed_mean },
    { label: "Fast Lap%", value: (row) => row.FastestLapPct },
  ];
  const bettingDriverExport = [
    { label: "Driver", value: (row) => row.Name },
    { label: "Team", value: (row) => row.TeamAbbrev },
    { label: "Win%", value: (row) => row.WinPct },
    { label: "Win Fair", value: (row) => probToDecimal(row.WinPct) },
    { label: "Win Am", value: (row) => probToAmerican(row.WinPct) },
    { label: "Top 3%", value: (row) => row.Top3Pct },
    { label: "Top 3 Fair", value: (row) => probToDecimal(row.Top3Pct) },
    { label: "Top 3 Am", value: (row) => probToAmerican(row.Top3Pct) },
    { label: "Top 5%", value: (row) => row.Top5Pct },
    { label: "Top 5 Fair", value: (row) => probToDecimal(row.Top5Pct) },
    { label: "Top 5 Am", value: (row) => probToAmerican(row.Top5Pct) },
    { label: "Top 10%", value: (row) => row.Top10Pct },
    { label: "Top 10 Fair", value: (row) => probToDecimal(row.Top10Pct) },
    { label: "Top 10 Am", value: (row) => probToAmerican(row.Top10Pct) },
  ];
  const bettingConstructorExport = [
    { label: "Constructor", value: (row) => row.Name },
    { label: "Team", value: (row) => row.TeamAbbrev },
    { label: "Win%", value: (row) => row.WinPct },
    { label: "Win Fair", value: (row) => probToDecimal(row.WinPct) },
    { label: "Win Am", value: (row) => probToAmerican(row.WinPct) },
    { label: "Both Classified%", value: (row) => row.Both_Classified_Pct },
    { label: "Classified Fair", value: (row) => probToDecimal(row.Both_Classified_Pct) },
    { label: "Classified Am", value: (row) => probToAmerican(row.Both_Classified_Pct) },
    { label: "Both Points%", value: (row) => row.Both_Points_Pct },
    { label: "Points Fair", value: (row) => probToDecimal(row.Both_Points_Pct) },
    { label: "Points Am", value: (row) => probToAmerican(row.Both_Points_Pct) },
    { label: "Both Podium%", value: (row) => row.Both_Podium_Pct },
    { label: "Podium Fair", value: (row) => probToDecimal(row.Both_Podium_Pct) },
    { label: "Podium Am", value: (row) => probToAmerican(row.Both_Podium_Pct) },
    { label: "Fast Lap%", value: (row) => row.FastestLapPct },
  ];
  const loading = driversLoading || constructorsLoading || distributionsLoading;
  const error = driversError || constructorsError || distributionsError;
  const outerClass = isDark
    ? "min-h-screen bg-slate-950 text-slate-100"
    : "min-h-screen bg-slate-100 text-slate-900";
  const exportDistributions = () => {
    const headers = ["Driver", ...Array.from({ length: 22 }, (_, i) => `P${i + 1}`)];
    const rows = sortedDistributionRows.map((row) => [
      row.Name,
      ...Array.from({ length: 22 }, (_, i) => row[`P${i + 1}`]),
    ]);
    downloadCsv("f1_driver_distributions.csv", headers, rows);
  };
  return (
    <div className={outerClass}>
      <div className="mx-auto w-full px-4 md:px-6 py-5">
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <div className="flex flex-col gap-1">
            <div className="flex items-baseline gap-3 flex-wrap">
              <h1 className="text-xl font-bold tracking-tight">
                {F1_CONFIG.title}
              </h1>
              {updatedAt ? (
                <div className={isDark ? "text-xs font-normal italic text-slate-100" : "text-xs font-normal italic text-slate-700"}>
                  Updated: {fmtUpdated(updatedAt)}
                </div>
              ) : null}
            </div>
          </div>
          <div className="ml-auto flex items-center gap-3 flex-wrap">
            <div
              className={
                "inline-flex items-center rounded-full px-1 py-1 text-[11px] ring-1 " +
                (isDark ? "bg-slate-900 ring-slate-700" : "bg-white ring-slate-300")
              }
            >
              <button
                type="button"
                onClick={() => setTheme("dark")}
                className={
                  "px-3 py-1 rounded-full font-medium transition " +
                  (isDark
                    ? "bg-sky-500 text-slate-950 shadow-sm"
                    : "text-slate-500 hover:text-slate-800")
                }
              >
                Dark
              </button>
              <button
                type="button"
                onClick={() => setTheme("light")}
                className={
                  "px-3 py-1 rounded-full font-medium transition " +
                  (!isDark
                    ? "bg-sky-500 text-slate-950 shadow-sm"
                    : "text-slate-400 hover:text-slate-100")
                }
              >
                Light
              </button>
            </div>
          </div>
        </div>
        <div className={"mb-4 flex flex-wrap items-center gap-3 rounded-2xl border px-3 py-2 shadow-sm " + (isDark ? "border-slate-800 bg-slate-900/95" : "border-slate-200 bg-white")}>
            <SegTabs
              tabs={[
                { key: "projections", label: "Projections" },
                { key: "betting", label: "Betting" },
                { key: "distributions", label: "Distributions" },
              ]}
              value={tab}
              onChange={setTab}
              isDark={isDark}
            />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search driver or constructor…"
              className={
                "h-8 rounded-md border px-3 text-xs w-full sm:w-64 sm:ml-auto focus:outline-none focus:ring-2 focus:ring-sky-500 " +
                (isDark
                  ? "bg-slate-950 border-slate-700 text-slate-100 placeholder:text-slate-500"
                  : "bg-white border-slate-300 text-slate-900 placeholder:text-slate-400")
              }
            />
        </div>
        {loading && <div className="text-sm text-slate-400">Loading…</div>}
        {error && (
          <div className="text-sm text-red-400">Failed to load: {String(error)}</div>
        )}
        {!loading && !error && tab === "projections" && (
          <div className="space-y-4">
            <div className={"flex flex-wrap items-center gap-3 rounded-2xl border px-3 py-1 shadow-sm " + (isDark ? "border-slate-800 bg-slate-900/95" : "border-slate-200 bg-white")}>
              <SegTabs
                tabs={[
                  { key: "drivers", label: "Drivers" },
                  { key: "constructors", label: "Constructors" },
                ]}
                value={projectionView}
                onChange={setProjectionView}
                isDark={isDark}
              />
              {projectionView === "drivers" ? (
                <SegTabs
                  tabs={[
                    { key: "driver", label: "Driver" },
                    { key: "captain", label: "Captain" },
                  ]}
                  value={driverMode}
                  onChange={(mode) => {
                    setDriverMode(mode);
                    setProjSort({
                      col: mode === "captain" ? "Captain_DK_Proj" : "Driver_DK_Proj",
                      dir: "desc",
                    });
                  }}
                  isDark={isDark}
                />
              ) : null}
            </div>
            {projectionView === "drivers" ? (
              <SectionCard
                title="Driver Projections"
                subtitle={
                  isCaptain
                    ? "Captain-adjusted DK columns with 1.5x salary and projection."
                    : ""
                }
                isDark={isDark}
              >
                <DataTable
                  columns={projectionDriverColumns}
                  rows={sortedDriverProjectionRows}
                  sort={projSort}
                  setSort={setProjSort}
                  isDark={isDark}
                  stickyFirst
                  exportFilename={isCaptain ? "f1_driver_captain_projections.csv" : "f1_driver_projections.csv"}
                  exportRows={projectionDriverExport}
                />
              </SectionCard>
            ) : (
              <SectionCard
                title="Constructor Projections"
                subtitle="DK constructor view with the main sim outputs."
                isDark={isDark}
              >
                <DataTable
                  columns={projectionConstructorColumns}
                  rows={sortedConstructorProjectionRows}
                  sort={constructorProjSort}
                  setSort={setConstructorProjSort}
                  isDark={isDark}
                  stickyFirst
                  exportFilename="f1_constructor_projections.csv"
                  exportRows={projectionConstructorExport}
                />
              </SectionCard>
            )}
          </div>
        )}
        {!loading && !error && tab === "betting" && (
          <div className="space-y-4">
            <div className={"flex flex-wrap items-center gap-3 rounded-2xl border px-3 py-1 shadow-sm " + (isDark ? "border-slate-800 bg-slate-900/95" : "border-slate-200 bg-white")}>
              <SegTabs
                tabs={[
                  { key: "drivers", label: "Drivers" },
                  { key: "constructors", label: "Constructors" },
                ]}
                value={bettingView}
                onChange={setBettingView}
                isDark={isDark}
              />
            </div>
            {bettingView === "drivers" ? (
              <SectionCard
                title="Driver Betting"
                subtitle="Percentages plus fair decimal and American pricing for the main markets."
                isDark={isDark}
              >
                <DataTable
                  columns={bettingDriverColumns}
                  rows={sortedDriverBettingRows}
                  sort={bettingSort}
                  setSort={setBettingSort}
                  isDark={isDark}
                  stickyFirst
                  exportFilename="f1_driver_betting.csv"
                  exportRows={bettingDriverExport}
                />
              </SectionCard>
            ) : (
              <SectionCard
                title="Constructor Betting"
                subtitle="Constructor markets with fair decimal and American values."
                isDark={isDark}
              >
                <DataTable
                  columns={bettingConstructorColumns}
                  rows={sortedConstructorBettingRows}
                  sort={constructorBettingSort}
                  setSort={setConstructorBettingSort}
                  isDark={isDark}
                  stickyFirst
                  exportFilename="f1_constructor_betting.csv"
                  exportRows={bettingConstructorExport}
                />
              </SectionCard>
            )}
          </div>
        )}
        {!loading && !error && tab === "distributions" && (
          <SectionCard
            title="Driver Finish Distribution Matrix"
            subtitle="Probability by finishing position."
            isDark={isDark}
            right={
              <div className="flex items-center gap-3">
                <div className={isDark ? "text-xs text-slate-400" : "text-xs text-slate-600"}>
                  Heat intensity scales to the highest probability in the field.
                </div>
                <ActionButton onClick={exportDistributions} isDark={isDark}>
                  Export CSV
                </ActionButton>
              </div>
            }
          >
            <div className={"overflow-auto max-h-[72vh] rounded-xl border " + (isDark ? "border-slate-800" : "border-slate-200")}>
              <table className="w-full text-xs border-separate" style={{ borderSpacing: 0 }}>
                <thead>
                  <tr>
                    <th
                      onClick={() =>
                        setDistSort((s) =>
                          s.col === "Name"
                            ? { col: "Name", dir: s.dir === "asc" ? "desc" : "asc" }
                            : { col: "Name", dir: "asc" }
                        )
                      }
                      className={[
                        "sticky top-0 left-0 z-20 px-3 py-2 text-left font-bold border-b cursor-pointer",
                        isDark ? "bg-slate-900 border-slate-800" : "bg-white border-slate-200",
                      ].join(" ")}
                    >
                      Driver
                    </th>
                    {Array.from({ length: 22 }, (_, i) => i + 1).map((pos) => (
                      <th
                        key={pos}
                        onClick={() =>
                          setDistSort((s) =>
                            s.col === `P${pos}`
                              ? { col: `P${pos}`, dir: s.dir === "asc" ? "desc" : "asc" }
                              : { col: `P${pos}`, dir: "desc" }
                          )
                        }
                        className={[
                          "sticky top-0 z-10 px-2 py-2 text-center font-bold border-b cursor-pointer whitespace-nowrap",
                          isDark ? "bg-slate-900 border-slate-800" : "bg-white border-slate-200",
                        ].join(" ")}
                      >
                        P{pos}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sortedDistributionRows.map((row, idx) => {
                    const rowBg =
                      idx % 2 === 0
                        ? isDark
                          ? "bg-slate-950"
                          : "bg-white"
                        : isDark
                        ? "bg-slate-900/70"
                        : "bg-slate-50";
                    return (
                      <tr key={row.__key || row.Name} className={rowBg}>
                        <td
                          className={[
                            "sticky left-0 z-[1] px-3 py-2 border-b min-w-[220px]",
                            rowBg,
                            isDark ? "border-slate-800/80" : "border-slate-200",
                          ].join(" ")}
                        >
                          <div className="flex items-center gap-2">
                            <LogoImg
                              src={`${F1_CONFIG.driverLogoBase}/${driverLogoKey(row.Name)}.png`}
                              alt={row.Name}
                              className="h-8 w-8 rounded-full object-cover border border-slate-700"
                            />
                            <div className="font-bold">{row.Name}</div>
                          </div>
                        </td>
                        {Array.from({ length: 22 }, (_, i) => i + 1).map((pos) => (
                          <td
                            key={`${row.__key}-P${pos}`}
                            className={
                              isDark
                                ? "px-1 py-1 border-b border-slate-800/80"
                                : "px-1 py-1 border-b border-slate-200"
                            }
                          >
                            <DistCell
                              value={row[`P${pos}`]}
                              max={distributionMax}
                              isDark={isDark}
                            />
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </SectionCard>
        )}
      </div>
    </div>
  );
}